import useNoindex from "../lib/useNoindex";
import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, errorMessage } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { AuthShell } from "../components/auth/AuthShell";
import { LogIn, Mail, ArrowLeft } from "lucide-react";

export default function Login() {
  useNoindex();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [step, setStep] = useState("email"); // "email" → "otp" | "password"
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [noAccount, setNoAccount] = useState(false);

  const routeAfterLogin = (user) => {
    const role = user.role;
    if (role === "admin") navigate("/admin");
    else if (role === "judge") navigate("/judge/dashboard");
    else if (user.payment_status !== "paid") navigate("/payment");
    else navigate("/team");
  };

  const requestOtp = async (e) => {
    if (e) e.preventDefault();
    setBusy(true);
    setNoAccount(false);
    try {
      // Designated review account signs in with a password instead of OTP.
      const { data: pw } = await api.get("/auth/password-login-enabled", { params: { email } });
      if (pw.enabled) { setStep("password"); setBusy(false); return; }
      await api.post("/auth/login", { college_email: email });
      toast.success("Code sent — check your inbox");
      setStep("otp");
    } catch (err) {
      if (err?.response?.status === 404) setNoAccount(true);
      toast.error(errorMessage(err));
    } finally { setBusy(false); }
  };

  const passwordLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/password-login", { email, password });
      login(data.user);
      toast.success("Signed in");
      routeAfterLogin(data.user);
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/verify-otp", { college_email: email, otp });
      login(data.user);
      toast.success("Signed in");
      routeAfterLogin(data.user);
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };

  return (
    <AuthShell testid="login-page">
        <div className="card-surface">
          <LogIn className="w-8 h-8 text-brand mb-3" strokeWidth={1.5} />
          <h1 className="font-display text-3xl text-white">Sign in</h1>
          <p className="text-sm text-gray-400 mt-2">
            Sign in to your IdeaThon account to continue.
          </p>

          {step === "email" ? (
            <form onSubmit={requestOtp} className="mt-6 space-y-4">
              <div>
                <label className="label-eyebrow block mb-2">College email</label>
                <div className="relative">
                  <Mail className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" strokeWidth={1.5} />
                  <input
                    type="email" required autoFocus
                    value={email} onChange={(e) => setEmail(e.target.value)}
                    placeholder="yourname@university.ac.in"
                    className="input-field" style={{ paddingLeft: "2.5rem" }} data-testid="login-email"
                  />
                </div>
                <p className="text-[13px] text-gray-500 mt-2" data-testid="login-email-help">We&apos;ll send a one-time code to your college email.</p>
              </div>
              {noAccount && (
                <p className="text-xs text-gray-400" data-testid="login-no-account">
                  We couldn&apos;t find an account with that email.{" "}
                  <Link to="/register" className="text-brand hover:underline" data-testid="login-register-link">Register here →</Link>
                </p>
              )}
              <button type="submit" disabled={busy || !email} className="btn-primary w-full min-h-[48px] flex items-center justify-center gap-2" data-testid="login-send-otp">
                {busy ? <span className="spinner" /> : "Continue →"}
              </button>
              <p className="text-xs text-gray-500 text-center">
                New here?{" "}
                <Link to="/register" className="text-brand hover:underline" data-testid="login-go-register">Create an account</Link>
              </p>
            </form>
          ) : step === "password" ? (
            <form onSubmit={passwordLogin} className="mt-6 space-y-4">
              <p className="text-sm text-gray-400 mb-1">Enter the password for <span className="text-gray-200">{email}</span>.</p>
              <div>
                <label className="label-eyebrow block mb-2">Password</label>
                <input
                  type="password" required autoFocus
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="input-field" data-testid="login-password"
                />
              </div>
              <button type="submit" disabled={busy || !password} className="btn-primary w-full min-h-[48px] flex items-center justify-center gap-2" data-testid="login-password-submit">
                {busy && <span className="spinner" />} Sign in
              </button>
              <button type="button" onClick={() => { setStep("email"); setPassword(""); }} className="text-xs text-gray-400 hover:text-white flex items-center gap-1.5 mx-auto" data-testid="login-back-email-pw">
                <ArrowLeft className="w-3 h-3" /> Use a different email
              </button>
            </form>
          ) : (
            <form onSubmit={verify} className="mt-6 space-y-4">
              <p className="text-sm text-gray-400 mb-1">Enter the verification code we sent to <span className="text-gray-200">{email}</span>.</p>
              <div>
                <label className="label-eyebrow block mb-2">Verification code</label>
                <input
                  type="text" inputMode="numeric" pattern="[0-9]{6}" maxLength={6} required autoFocus
                  value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                  placeholder="••••••"
                  className="input-field text-center text-2xl font-mono tracking-[0.5em]"
                  data-testid="login-otp"
                />
              </div>
              <button type="submit" disabled={busy || otp.length < 6} className="btn-primary w-full min-h-[48px] flex items-center justify-center gap-2" data-testid="login-submit">
                {busy && <span className="spinner" />} Verify &amp; sign in
              </button>
              <button type="button" onClick={() => { setStep("email"); setOtp(""); }} className="text-xs text-gray-400 hover:text-white flex items-center gap-1.5 mx-auto" data-testid="login-back-email">
                <ArrowLeft className="w-3 h-3" /> Use a different email
              </button>
              <button type="button" onClick={() => requestOtp()} disabled={busy} className="text-xs text-brand hover:underline block mx-auto" data-testid="login-resend-otp">
                Resend code
              </button>
            </form>
          )}
        </div>
    </AuthShell>
  );
}
